import { Model } from "@lumora/core";

export type PortfolioKind = "image" | "beforeAfter" | "spin";

export interface PortfolioItemInit {
  id: string;
  kind: PortfolioKind;
  caption: string;
  alt: string;
  /** image: the still. beforeAfter: the "after" render. spin: MP4 url. */
  src: string;
  /** beforeAfter only: the supplied reference photo. */
  beforeSrc?: string;
  /** spin only: poster still shown before play / when reduced motion is on. */
  poster?: string;
  width?: number;
  height?: number;
}

/** Hard asset budgets (bytes) enforced by tests against public/. */
export const MAX_IMAGE_BYTES = 1.5 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 4 * 1024 * 1024;

/** One piece of work in the portfolio grid. Data-driven: see portfolio/items.ts. */
export class PortfolioItem extends Model {
  readonly id: string;
  readonly kind: PortfolioKind;
  readonly caption: string;
  readonly alt: string;
  readonly src: string;
  readonly beforeSrc: string;
  readonly poster: string;
  readonly width: number;
  readonly height: number;

  constructor(init: PortfolioItemInit) {
    super();
    if (init.kind === "beforeAfter" && !init.beforeSrc) throw new Error(`${init.id}: beforeAfter needs beforeSrc`);
    if (init.kind === "spin" && !init.poster) throw new Error(`${init.id}: spin needs a poster`);
    this.id = init.id;
    this.kind = init.kind;
    this.caption = init.caption;
    this.alt = init.alt;
    this.src = init.src;
    this.beforeSrc = init.beforeSrc ?? "";
    this.poster = init.poster ?? "";
    this.width = init.width ?? 1600;
    this.height = init.height ?? 900;
  }

  validate(): string[] {
    return this.alt.trim() === "" ? [`${this.id}: alt text is required`] : [];
  }

  isSpin(): boolean { return this.kind === "spin"; }
  isBeforeAfter(): boolean { return this.kind === "beforeAfter"; }

  /** Every public asset path this item needs. */
  assetPaths(): string[] {
    return [this.src, this.beforeSrc, this.poster].filter((p) => p !== "");
  }

  /** Byte budget for one asset path by extension. */
  static budgetFor(path: string): number {
    return /\.(mp4|webm)$/i.test(path) ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  }
}
